/**
 * proef's aroma & flavour lexicon.
 *
 * Hierarchical: origin (primary = grape & fermentation, secondary = winemaking
 * after fermentation, tertiary = ageing) → cluster → descriptor. The user is
 * always steered cluster-first, because sensory research shows novices agree
 * on clusters ("citrus") far more than on descriptors ("pink grapefruit"), and
 * a cluster-level match is enough for recommendation.
 *
 * Wording is deliberately our own. WSET's SAT lexicon and Noble's Aroma Wheel
 * are copyrighted/trademarked works; the *phenomenon* (grapes smell of fruit,
 * oak smells of vanilla) is not. See docs/research/tasting-methodologies.md.
 */

export const AROMA_ORIGIN = ["primary", "secondary", "tertiary"] as const;
export type AromaOrigin = (typeof AROMA_ORIGIN)[number];

export interface AromaCluster {
  readonly id: string;
  readonly label: string;
  readonly origin: AromaOrigin;
  readonly descriptors: readonly string[];
}

/** `cluster.descriptor`, e.g. `citrus.lime`. Stable across lexicon revisions. */
export type DescriptorId = `${string}.${string}`;

export const AROMA_CLUSTERS: readonly AromaCluster[] = [
  // ---- primary: from the grape and fermentation ----
  {
    id: "floral",
    label: "Floral",
    origin: "primary",
    descriptors: ["blossom", "elderflower", "honeysuckle", "jasmine", "rose", "violet", "lavender"],
  },
  {
    id: "green-fruit",
    label: "Green fruit",
    origin: "primary",
    descriptors: ["green-apple", "red-apple", "pear", "gooseberry", "grape", "quince"],
  },
  {
    id: "citrus",
    label: "Citrus",
    origin: "primary",
    descriptors: ["lemon", "lime", "grapefruit", "orange", "orange-peel", "lemon-zest"],
  },
  {
    id: "stone-fruit",
    label: "Stone fruit",
    origin: "primary",
    descriptors: ["peach", "apricot", "nectarine", "white-plum", "white-cherry"],
  },
  {
    id: "tropical-fruit",
    label: "Tropical fruit",
    origin: "primary",
    descriptors: ["pineapple", "mango", "passion-fruit", "banana", "lychee", "melon", "guava"],
  },
  {
    id: "red-fruit",
    label: "Red fruit",
    origin: "primary",
    descriptors: ["strawberry", "raspberry", "redcurrant", "cranberry", "red-cherry", "red-plum"],
  },
  {
    id: "black-fruit",
    label: "Black fruit",
    origin: "primary",
    descriptors: [
      "blackcurrant",
      "blackberry",
      "black-cherry",
      "black-plum",
      "blueberry",
      "mulberry",
    ],
  },
  {
    id: "herbaceous",
    label: "Herbaceous",
    origin: "primary",
    descriptors: [
      "cut-grass",
      "bell-pepper",
      "asparagus",
      "tomato-leaf",
      "blackcurrant-leaf",
      "nettle",
    ],
  },
  {
    id: "herbal",
    label: "Herbal",
    origin: "primary",
    descriptors: ["eucalyptus", "mint", "fennel", "dill", "thyme", "rosemary", "garrigue"],
  },
  {
    id: "spice-fresh",
    label: "Pungent spice",
    origin: "primary",
    descriptors: ["black-pepper", "white-pepper", "liquorice", "ginger"],
  },
  {
    id: "mineral",
    label: "Mineral & other",
    origin: "primary",
    descriptors: ["wet-stone", "flint", "chalk", "saline", "petrol", "beeswax"],
  },
  // ---- secondary: winemaking after fermentation ----
  {
    id: "yeast",
    label: "Yeast & lees",
    origin: "secondary",
    descriptors: ["bread-dough", "brioche", "biscuit", "toasted-bread", "yoghurt", "cheese-rind"],
  },
  {
    id: "malolactic",
    label: "Malolactic",
    origin: "secondary",
    descriptors: ["butter", "cream", "hazelnut-cream"],
  },
  {
    id: "oak",
    label: "Oak",
    origin: "secondary",
    descriptors: [
      "vanilla",
      "toast",
      "smoke",
      "cedar",
      "coconut",
      "clove",
      "cinnamon",
      "charred-wood",
    ],
  },
  // ---- tertiary: ageing ----
  {
    id: "oxidative",
    label: "Deliberate oxidation",
    origin: "tertiary",
    descriptors: ["almond", "walnut", "hazelnut", "marzipan", "caramel", "toffee", "coffee"],
  },
  {
    id: "fruit-development",
    label: "Fruit development",
    origin: "tertiary",
    descriptors: ["dried-apricot", "raisin", "fig", "prune", "cooked-fruit", "jam", "marmalade"],
  },
  {
    id: "bottle-age",
    label: "Bottle age",
    origin: "tertiary",
    descriptors: [
      "honey",
      "kerosene",
      "mushroom",
      "forest-floor",
      "leather",
      "tobacco",
      "meat",
      "truffle",
      "wet-leaves",
      "cigar-box",
    ],
  },
];

/**
 * The descriptor half that names a whole cluster: a Quick tasting picks families, not descriptors,
 * and this keeps one id shape for both. It is never offered in a pick-list.
 */
export const CLUSTER_WIDE = "*";

/** `cluster.*`, the id that stands for the cluster as a whole. */
export function clusterWideId(clusterId: string): DescriptorId {
  return `${clusterId}.${CLUSTER_WIDE}`;
}

export function isClusterWide(id: string): boolean {
  return id.endsWith(`.${CLUSTER_WIDE}`) && resolveDescriptor(id) !== undefined;
}

const byId: ReadonlyMap<string, AromaCluster> = new Map(AROMA_CLUSTERS.map((c) => [c.id, c]));

export function clusterById(id: string): AromaCluster | undefined {
  return byId.get(id);
}

/** Parse `cluster.descriptor` into its parts, or `undefined` if the id is not in the lexicon. */
export function resolveDescriptor(
  id: string,
): { cluster: AromaCluster; descriptor: string } | undefined {
  const dot = id.indexOf(".");
  if (dot <= 0 || dot === id.length - 1) return undefined;
  const cluster = byId.get(id.slice(0, dot));
  const descriptor = id.slice(dot + 1);
  if (cluster === undefined) return undefined;
  if (descriptor !== CLUSTER_WIDE && !cluster.descriptors.includes(descriptor)) return undefined;
  return { cluster, descriptor };
}

export function isDescriptorId(id: string): id is DescriptorId {
  return resolveDescriptor(id) !== undefined;
}

/** Every valid descriptor id, in lexicon order — the pick-list source for the UI. */
export function allDescriptorIds(): DescriptorId[] {
  return AROMA_CLUSTERS.flatMap((c) => c.descriptors.map((d): DescriptorId => `${c.id}.${d}`));
}

/** Distinct clusters touched by a set of descriptors, in lexicon order. */
export function clustersOf(ids: readonly string[]): AromaCluster[] {
  const seen = new Set<string>();
  for (const id of ids) {
    const r = resolveDescriptor(id);
    if (r !== undefined) seen.add(r.cluster.id);
  }
  return AROMA_CLUSTERS.filter((c) => seen.has(c.id));
}
